import { crdt } from '@w/crdt'
import { decodeRecord, encodeRecord, type RecordValue } from '../shared/sqlite-record'

export type Query = <T>(sql: string, params?: unknown[]) => Promise<T[]>
/** The replica's SQL connection: reads, and an atomic transaction for each re-expressed row. */
export type PendingStore = { query: Query; transaction<T>(fn: (tx: { query: Query }) => Promise<T>): Promise<T> }
export type Statement = { sql: string; params: unknown[] }
export type RowValues = Record<string, RecordValue>
/** A table as its declaration lays it out (primary key and physical replicated columns). */
export type PendingTableLayout = { name: string; primaryKey: readonly string[]; columns: readonly { name: string }[] }
/**
 * One row the replica holds unacknowledged changes for. `base` is the value it last synced: the before-image of
 * its first pending CDC change (null when the row was inserted unsynced). `local` is its current value (null
 * when it was deleted unsynced).
 */
export type PendingRow = { table: string; key: RowValues; base: RowValues | null; local: RowValues | null }
/** A pending row as persisted for the reset; `stage` 1 means its table's own step (e.g. a CRDT rebase) committed. */
export type PersistedPendingRow = PendingRow & { id: number; stage: number }
/** A table's own step for a row (e.g. the product's CRDT rebase). It must commit `done` in its own transaction. */
export type PendingRowStep = (row: PersistedPendingRow, done: Statement, rows: readonly PersistedPendingRow[]) => Promise<void>

/**
 * Captured work lives in a technical table until it is re-expressed. The `_turso_` prefix makes it engine-internal:
 * never replicated (fork sync/engine/src/replication.rs:540 `replicated`) and kept by an in-place replica reset,
 * which empties only non-internal tables (replication.rs:975-990).
 */
export const pendingWorkTable = '_turso_reset_pending'
const pendingWorkDdl = `CREATE TABLE IF NOT EXISTS ${pendingWorkTable}(id INTEGER PRIMARY KEY, tbl TEXT NOT NULL, names TEXT NOT NULL, key BLOB NOT NULL, base BLOB, local BLOB, stage INTEGER NOT NULL DEFAULT 0)`

// Turso CDC change_type: 1 insert, 0 update, -1 delete; commit markers carry no table.
const cdcInsert = 1

/**
 * db-crdt-sync spec :37 (a reset never loses work): read every pending row before the replica resets. Turso CDC
 * keeps full before-images until the server acknowledges the change (fork docs/replication-semantics.md:58-64).
 */
export async function capturePendingRows(query: Query, tables: readonly PendingTableLayout[]): Promise<PendingRow[]> {
  const layouts = new Map(tables.map(table => [table.name, table]))
  const changes = await query<{ change_id: number; table_name: string; id: number; change_type: number; before: Uint8Array | null; after: Uint8Array | null }>(
    'SELECT change_id,table_name,id,change_type,before,after FROM turso_cdc WHERE table_name IS NOT NULL ORDER BY change_id')
  const byRow = new Map<string, (typeof changes)[number][]>()
  for (const change of changes) {
    const at = `${change.table_name}\u0000${change.id}`
    byRow.set(at, [...(byRow.get(at) ?? []), change])
  }
  const accepted = await acceptedThrough(query)
  const stored = new Map<string, string[]>()
  const pending: PendingRow[] = []
  for (const rowChanges of byRow.values()) {
    const change = rowChanges[0]
    const layout = layouts.get(change.table_name)
    if (!layout) continue
    let recordColumns = stored.get(layout.name)
    if (!recordColumns) {
      // A record stores every column except virtual generated ones (hidden 2), in declaration order.
      recordColumns = (await query<{ name: string; hidden: number }>(`PRAGMA table_xinfo(${quote(layout.name)})`))
        .filter(column => column.hidden !== 2).map(column => column.name)
      stored.set(layout.name, recordColumns)
    }
    const names = layout.columns.map(column => column.name)
    const before = change.before && change.change_type !== cdcInsert ? decodeRecord(change.before) : null
    let base: RowValues | null = before ? Object.fromEntries(names.map(name => [name, before[safeIndex(recordColumns, name)] ?? null])) : null
    // Changes the server already accepted (DST seed 2053: a push accepted, then a 410 before its receipt cleared the
    // CDC) are part of the synced value: the base advances through them, and a row with nothing after them is synced.
    const columns = recordColumns
    const done = rowChanges.filter(each => each.change_id <= accepted)
    for (const each of done) base = advance(base, each, names, columns)
    if (done.length === rowChanges.length && done.length > 0) continue
    const [local] = await query<RowValues>(`SELECT ${names.map(name => quote(name)).join(',')} FROM ${quote(layout.name)} WHERE rowid=?`, [change.id])
    const source = local ?? base
    if (!source) continue
    pending.push({ table: layout.name, key: Object.fromEntries(layout.primaryKey.map(name => [name, source[name] ?? null])), base, local: local ? normalize(local) : null })
  }
  return pending
}

/**
 * The CDC change id the server's accepted pushes cover. The engine freezes one outbox entry per committed CDC
 * transaction, in order (fork sync/engine/src/replication.rs `freeze`), so the accepted prefix of the outbox is the
 * first k frozen data transactions. When the counts disagree nothing is assumed accepted.
 */
async function acceptedThrough(query: Query): Promise<number> {
  const outbox = await query<{ accepted: number | null }>('SELECT accepted_revision AS accepted FROM _turso_replication_outbox ORDER BY sequence')
  const prefix = outbox.findIndex(entry => entry.accepted === null)
  const k = prefix < 0 ? outbox.length : prefix
  if (k === 0) return 0
  const [state] = await query<{ frozen: number }>('SELECT frozen_through AS frozen FROM _turso_replication_state WHERE id=1')
  const frozen = await query<{ hi: number }>(`SELECT max(change_id) AS hi FROM turso_cdc WHERE change_txn_id>=0 GROUP BY change_txn_id
    HAVING max(change_id)<=? AND sum(table_name IS NOT NULL AND table_name<>'sqlite_schema' AND table_name NOT LIKE '\\_turso\\_%' ESCAPE '\\')>0 ORDER BY change_txn_id`, [state?.frozen ?? 0])
  if (frozen.length !== outbox.length) return 0
  return frozen[k - 1].hi
}

/** `base` after one accepted CDC change: an INSERT's or UPDATE's after-image, a CRDT column's update folded in. */
function advance(base: RowValues | null, change: { change_type: number; after: Uint8Array | null }, names: readonly string[], columns: readonly string[]): RowValues | null {
  if (change.change_type === -1 || !change.after) return null
  const after = decodeRecord(change.after)
  return Object.fromEntries(names.map(name => {
    const next = after[safeIndex(columns, name)] ?? null
    const previous = base?.[name]
    if (next instanceof Uint8Array && !isSnapshot(next) && previous instanceof Uint8Array && isSnapshot(previous)) {
      const doc = crdt.load(previous)
      try { doc.applyUpdate(next); return [name, doc.encodeSnapshot().raw()] } finally { doc.free() }
    }
    return [name, next]
  }))
}

const snapshotMagic = new TextEncoder().encode('CRS3')
function isSnapshot(bytes: Uint8Array) { return snapshotMagic.every((byte, index) => bytes[index] === byte) }

/**
 * Capture the replica's pending rows into the technical table, durably, before a reset starts. Work persisted by
 * an earlier failed reset is kept: a row captured again keeps its first base and takes the newer local value.
 */
export async function persistPendingRows(store: PendingStore, tables: readonly PendingTableLayout[]): Promise<number> {
  await store.query(pendingWorkDdl)
  return writePendingRows(store, tables, await capturePendingRows(store.query, tables))
}

/**
 * Capture every local row of `names` as work the server lacks (base null, so re-express writes it whole): after a
 * production reset the server deleted its copy, so a synced row is the device's only copy. Rows are matched by
 * `where` (e.g. only child edges); held rows keep their base and take the current local value.
 */
export async function persistLocalRows(store: PendingStore, tables: readonly PendingTableLayout[], names: Readonly<Record<string, string>>): Promise<number> {
  await store.query(pendingWorkDdl)
  const rows: PendingRow[] = []
  for (const table of tables) {
    const where = names[table.name]
    if (where === undefined) continue
    const columns = table.columns.map(column => column.name)
    for (const local of await store.query<RowValues>(`SELECT ${columns.map(name => quote(name)).join(',')} FROM ${quote(table.name)} WHERE ${where}`))
      rows.push({ table: table.name, key: Object.fromEntries(table.primaryKey.map(name => [name, local[name] ?? null])), base: null, local: normalize(local) })
  }
  return writePendingRows(store, tables, rows)
}

/** Holds rows read elsewhere (another replica's file) as work this replica lacks, in one transaction; a row already
 * held (same table and key) takes the new local value. Re-express writes them like any persisted row. */
export function persistRows(store: PendingStore, tables: readonly PendingTableLayout[], rows: readonly PendingRow[]): Promise<number> {
  return store.query(pendingWorkDdl).then(() => writePendingRows(store, tables, rows))
}

async function writePendingRows(store: PendingStore, tables: readonly PendingTableLayout[], rows: readonly PendingRow[]): Promise<number> {
  const layouts = new Map(tables.map(table => [table.name, table]))
  await store.transaction(async tx => {
    for (const row of rows) {
      const names = safe(layouts.get(row.table)).columns.map(column => column.name)
      const key = encodeRecord(Object.values(row.key))
      const local = row.local ? encodeRecord(names.map(name => row.local?.[name] ?? null)) : null
      const [held] = await tx.query<{ id: number }>(`SELECT id FROM ${pendingWorkTable} WHERE tbl=? AND key=?`, [row.table, key])
      if (held) await tx.query(`UPDATE ${pendingWorkTable} SET names=?,local=? WHERE id=?`, [JSON.stringify(names), local, held.id])
      else await tx.query(`INSERT INTO ${pendingWorkTable}(tbl,names,key,base,local) VALUES(?,?,?,?,?)`,
        [row.table, JSON.stringify(names), key, row.base ? encodeRecord(names.map(name => row.base?.[name] ?? null)) : null, local])
    }
  })
  return rows.length
}

export async function loadPersistedPendingRows(store: Pick<PendingStore, 'query'>, tables: readonly PendingTableLayout[]): Promise<PersistedPendingRow[]> {
  const [present] = await store.query<{ name: string }>("SELECT name FROM sqlite_schema WHERE type='table' AND name=?", [pendingWorkTable])
  if (!present) return []
  const layouts = new Map(tables.map(table => [table.name, table]))
  const held = await store.query<{ id: number; tbl: string; names: string; key: Uint8Array; base: Uint8Array | null; local: Uint8Array | null; stage: number }>(
    `SELECT id,tbl,names,key,base,local,stage FROM ${pendingWorkTable} ORDER BY id`)
  return held.flatMap(row => {
    const layout = layouts.get(row.tbl)
    if (!layout) return []
    const names = JSON.parse(row.names) as string[]
    const values = (bytes: Uint8Array | null) => {
      if (!bytes) return null
      const decoded = decodeRecord(new Uint8Array(bytes))
      return Object.fromEntries(names.map((name, index) => [name, decoded[index] ?? null]))
    }
    const key = decodeRecord(new Uint8Array(row.key))
    return [{ id: row.id, stage: row.stage, table: row.tbl, key: Object.fromEntries(layout.primaryKey.map((name, index) => [name, key[index] ?? null])), base: values(row.base), local: values(row.local) }]
  })
}

/**
 * After the next successful open from the snapshot, re-express the persisted work exactly once: each row's write
 * and the deletion of its persisted copy commit in one transaction, so a crash or a second run never applies a
 * row twice. Rows re-apply as their changed columns only (requirement :63, partial updates) through the replica's
 * ordinary write path, so local triggers and the server's rules judge them like any write; columns a peer
 * changed and this device did not stay the peer's. A row inserted unsynced is written whole. Delete wins both
 * ways, as in crdt_rs: a row deleted unsynced is deleted even if a peer changed it, and a pending update to a row
 * the server deleted is refused with "deleted on server". `steps` runs a table's own step first (the product's
 * CRDT rebase), `skip` names tables derived on the write path (re-deriving them from their source re-creates
 * them), `exclude` names columns a step owns. A refused write is returned, never dropped silently.
 */
export async function reexpressPersistedRows(store: PendingStore, tables: readonly PendingTableLayout[], options: {
  steps?: Readonly<Record<string, PendingRowStep>>; skip?: ReadonlySet<string>; exclude?: Readonly<Record<string, readonly string[]>> } = {}) {
  const layouts = new Map(tables.map(table => [table.name, table]))
  const rows = await loadPersistedPendingRows(store, tables)
  const applied: PendingRow[] = [], refused: { row: PendingRow; error: unknown }[] = []
  for (const row of rows) {
    const step = options.steps?.[row.table]
    if (step && row.stage === 0 && row.local) await step(row, { sql: `UPDATE ${pendingWorkTable} SET stage=1 WHERE id=?`, params: [row.id] }, rows)
  }
  for (const row of rows) {
    const done = { sql: `DELETE FROM ${pendingWorkTable} WHERE id=?`, params: [row.id] }
    const layout = safe(layouts.get(row.table))
    try {
      const plan = options.skip?.has(row.table) ? [] : await planRow(store.query, layout, row, new Set(options.exclude?.[row.table]))
      if (plan instanceof Error) { refused.push({ row, error: plan }); await store.query(done.sql, done.params); continue }
      await store.transaction(async tx => { for (const statement of [...plan, done]) await tx.query(statement.sql, statement.params) })
      if (plan.length > 0) applied.push(row)
    } catch (error) {
      refused.push({ row, error })
      await store.query(done.sql, done.params)
    }
  }
  return { applied, refused }
}

/** A pending update to a row the server deleted meanwhile: delete wins, so the update is reported, not re-applied. */
export class PendingRowDeletedOnServer extends Error {
  constructor(table: string) { super(`deleted on server: ${table}`); this.name = 'PendingRowDeletedOnServer' }
}

async function planRow(query: Query, layout: PendingTableLayout, row: PendingRow, excluded: ReadonlySet<string>): Promise<Statement[] | Error> {
  const keys = layout.primaryKey
  const where = keys.map(name => `${quote(name)}=?`).join(' AND ')
  const key = keys.map(name => row.key[name])
  if (!row.local) return [{ sql: `DELETE FROM ${quote(layout.name)} WHERE ${where}`, params: key }]
  const local = row.local
  const columns = layout.columns.map(column => column.name).filter(name => !excluded.has(name))
  const changed = columns.filter(name => !keys.includes(name) && (!row.base || !same(row.base[name], local[name])))
  if (row.base && changed.length === 0) return []
  // An existing row takes only the changed columns; a row inserted unsynced is written whole. A row the device had
  // synced that the server no longer has was deleted there: delete wins, so it is reported, never re-inserted.
  // (An upsert would check NOT NULL on its insert half before the conflict, so a row whose excluded column a step
  // wrote would fail.)
  const [exists] = await query(`SELECT 1 FROM ${quote(layout.name)} WHERE ${where}`, key)
  if (exists) return changed.length === 0 ? [] : [{ sql: `UPDATE ${quote(layout.name)} SET ${changed.map(name => `${quote(name)}=?`).join(',')} WHERE ${where}`, params: [...changed.map(name => local[name]), ...key] }]
  if (row.base) return new PendingRowDeletedOnServer(row.table)
  return [{ sql: `INSERT INTO ${quote(layout.name)}(${columns.map(name => quote(name)).join(',')}) VALUES(${columns.map(() => '?').join(',')})`, params: columns.map(name => local[name]) }]
}

function same(left: RecordValue | undefined, right: RecordValue | undefined): boolean {
  if (left instanceof Uint8Array && right instanceof Uint8Array) return left.length === right.length && left.every((byte, index) => byte === right[index])
  return (left ?? null) === (right ?? null)
}

function normalize(row: RowValues): RowValues {
  return Object.fromEntries(Object.entries(row).map(([name, value]) => [name, value instanceof Uint8Array ? new Uint8Array(value) : value]))
}

function safeIndex(columns: readonly string[], name: string): number {
  const index = columns.indexOf(name)
  if (index < 0) throw new Error(`INV: column ${name} is missing from the stored record layout`)
  return index
}

function safe<T>(value: T | undefined): T {
  if (value === undefined) throw new Error('INV: persisted pending row names an undeclared table')
  return value
}

function quote(name: string): string { return `"${name.replaceAll('"', '""')}"` }

/** The persisted work as raw rows, to carry it into a replacement replica file (the browser reset replaces the file). */
export async function exportPendingWork(store: Pick<PendingStore, 'query'>): Promise<Record<string, unknown>[]> {
  const [present] = await store.query<{ name: string }>("SELECT name FROM sqlite_schema WHERE type='table' AND name=?", [pendingWorkTable])
  return present ? store.query<Record<string, unknown>>(`SELECT id,tbl,names,key,base,local,stage FROM ${pendingWorkTable} ORDER BY id`) : []
}

/** Writes carried rows into this replica's technical table in one transaction; rows already present are kept. */
export async function importPendingWork(store: PendingStore, rows: readonly Record<string, unknown>[]): Promise<void> {
  if (rows.length === 0) return
  await store.query(pendingWorkDdl)
  await store.transaction(async tx => {
    for (const row of rows) await tx.query(`INSERT OR IGNORE INTO ${pendingWorkTable}(id,tbl,names,key,base,local,stage) VALUES(?,?,?,?,?,?,?)`,
      [row.id, row.tbl, row.names, row.key, row.base, row.local, row.stage])
  })
}
